import React from 'react';
import { CreditCard, Calendar, GlobeIcon, BarChart2 } from 'lucide-react';

/**
 * Секция формы с информацией о доходах
 */
const IncomeSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  periodTypes
}) => {
  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <CreditCard className="w-5 h-5 mr-2 text-green-500" />
        Доходы
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Сумма дохода */}
        <div>
          <label htmlFor="incomeAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Сумма дохода
          </label>
          <input
            type="text"
            id="incomeAmount"
            name="incomeAmount"
            value={formData.incomeAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.incomeAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму дохода"
          />
          {errors.incomeAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.incomeAmount}
            </p>
          )}
        </div>

        {/* Период дохода */}
        <div>
          <label htmlFor="incomePeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Период
          </label>
          <select
            id="incomePeriod"
            name="incomePeriod"
            value={formData.incomePeriod}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.incomePeriod ? 'input-error' : ''}`}
          >
            {periodTypes.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name}
              </option>
            ))}
          </select>
          {errors.incomePeriod && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.incomePeriod}
            </p>
          )}
        </div>

        {/* Пользовательский период */}
        {formData.incomePeriod === 'custom' && (
          <>
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Дата начала
              </label>
              <input
                type="date"
                id="startDate"
                name="customPeriod.startDate"
                value={formData.customPeriod.startDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors['customPeriod.startDate'] ? 'input-error' : ''}`}
              />
              {errors['customPeriod.startDate'] && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors['customPeriod.startDate']}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Дата окончания
              </label>
              <input
                type="date"
                id="endDate"
                name="customPeriod.endDate"
                value={formData.customPeriod.endDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors['customPeriod.endDate'] ? 'input-error' : ''}`}
              />
              {errors['customPeriod.endDate'] && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors['customPeriod.endDate']}
                </p>
              )}
            </div>
          </>
        )}

        {/* Источник дохода */}
        <div>
          <label htmlFor="incomeSource" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Источник дохода
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="incomeSource"
                value="russia"
                checked={formData.incomeSource === 'russia'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Из России
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="incomeSource"
                value="foreign"
                checked={formData.incomeSource === 'foreign'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Из-за границы
              </span>
            </label>
          </div>
          {errors.incomeSource && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.incomeSource}
            </p>
          )}
        </div>

        {/* Прогноз роста доходов */}
        <div>
          <label htmlFor="incomeGrowthRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <BarChart2 className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
            Прогноз роста (%)
          </label>
          <input
            type="number"
            id="incomeGrowthRate"
            name="incomeGrowthRate"
            value={formData.incomeGrowthRate}
            onChange={handleInputChange}
            min="0"
            max="100"
            className="form-input w-full"
            placeholder="0"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Для расчета прогнозируемого дохода
          </p>
        </div>
      </div>

      {/* Информация о лимитах */}
      <div className="mt-4 bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 text-sm">
        <div className="font-medium mb-1">Лимиты дохода:</div>
        <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-1">
          <li>• Самозанятость (НПД): <span className="font-medium">2,4 млн ₽</span> в год</li>
          <li>• УСН (любой тип): <span className="font-medium">219,2 млн ₽</span> в год</li>
        </ul>
      </div>
    </div>
  );
};

export default IncomeSection;